import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { GridBackground } from "../EAKCArchitecture/components/GridBackground";
import { COLORS, FONTS, SPRING } from "./constants";

const SHIELD_CX = 960;
const SHIELD_CY = 440;
const BADGE_RADIUS = 250;

const BADGES = [
  { icon: "\uD83D\uDD12", label: "Row-Level Security", sub: "Every query filtered by tenant", angle: 270, delay: 50 },
  { icon: "\uD83C\uDFE2", label: "Multi-Tenant Isolation", sub: "Each company completely separate", angle: 330, delay: 70 },
  { icon: "\uD83D\uDCCB", label: "Audit Logging", sub: "Full activity trail", angle: 30, delay: 90 },
  { icon: "\uD83D\uDD11", label: "Role-Based Access", sub: "User / Admin / Super Admin", angle: 90, delay: 110 },
  { icon: "\uD83D\uDEAB", label: "No Data Training", sub: "Your knowledge stays private", angle: 150, delay: 130 },
  { icon: "\u26A1", label: "Source Permissions", sub: "Control who sees what", angle: 210, delay: 150 },
];

export const Scene11Security: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Title
  const titleProgress = spring({
    frame: frame - 5,
    fps,
    config: SPRING.snappy,
  });
  const titleOpacity = interpolate(titleProgress, [0, 1], [0, 1]);
  const titleY = interpolate(titleProgress, [0, 1], [20, 0]);

  // Subtitle
  const subtitleOpacity = interpolate(frame, [20, 50], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Shield
  const shieldProgress = spring({
    frame: frame - 20,
    fps,
    config: SPRING.gentle,
  });
  const shieldScale = interpolate(shieldProgress, [0, 1], [0.3, 1]);
  const shieldOpacity = interpolate(shieldProgress, [0, 1], [0, 1]);

  // Shield glow pulse
  const glowIntensity = interpolate(
    Math.sin(frame * 0.05),
    [-1, 1],
    [10, 25],
  );

  // Global fade out
  const fadeOut = interpolate(frame, [550, 600], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill>
      <GridBackground />

      <div style={{ opacity: fadeOut }}>
        {/* Title */}
        <div
          style={{
            position: "absolute",
            top: 60,
            left: 0,
            right: 0,
            textAlign: "center",
            fontFamily: FONTS.heading,
            fontSize: 40,
            fontWeight: 700,
            color: COLORS.textPrimary,
            opacity: titleOpacity,
            transform: `translateY(${titleY}px)`,
          }}
        >
          Enterprise-Grade Security
        </div>

        {/* Subtitle */}
        <div
          style={{
            position: "absolute",
            top: 110,
            left: 0,
            right: 0,
            textAlign: "center",
            fontFamily: FONTS.body,
            fontSize: 20,
            color: COLORS.accentGreen,
            opacity: subtitleOpacity,
          }}
        >
          Your data is yours. We never train on it.
        </div>

        {/* Central Shield */}
        <div
          style={{
            position: "absolute",
            left: SHIELD_CX - 80,
            top: SHIELD_CY - 90,
            width: 160,
            height: 180,
            opacity: shieldOpacity,
            transform: `scale(${shieldScale})`,
            filter: `drop-shadow(0 0 ${glowIntensity}px ${COLORS.accentGreen}60)`,
          }}
        >
          <svg viewBox="0 0 160 180" width={160} height={180}>
            <defs>
              <linearGradient id="shieldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor={COLORS.accentGreen} stopOpacity={0.3} />
                <stop offset="100%" stopColor={COLORS.accentTeal} stopOpacity={0.1} />
              </linearGradient>
            </defs>
            <path
              d="M 80 10 L 150 50 L 140 130 Q 80 170 80 170 Q 80 170 20 130 L 10 50 Z"
              fill="url(#shieldGrad)"
              stroke={COLORS.accentGreen}
              strokeOpacity={0.5}
              strokeWidth={2}
            />
          </svg>
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: 160,
              height: 180,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 48,
            }}
          >
            {"\uD83D\uDD12"}
          </div>
        </div>

        {/* Dashed lines from shield to badges (SVG overlay) */}
        <svg
          style={{ position: "absolute", top: 0, left: 0, width: 1920, height: 1080, pointerEvents: "none" }}
        >
          {BADGES.map((badge) => {
            const rad = (badge.angle * Math.PI) / 180;
            const bx = SHIELD_CX + Math.cos(rad) * BADGE_RADIUS;
            const by = SHIELD_CY + Math.sin(rad) * BADGE_RADIUS;

            const lineProgress = spring({
              frame: frame - badge.delay,
              fps,
              config: SPRING.gentle,
            });
            const lineOpacity = interpolate(lineProgress, [0, 1], [0, 0.4]);

            return (
              <line
                key={badge.label}
                x1={SHIELD_CX}
                y1={SHIELD_CY}
                x2={SHIELD_CX + (bx - SHIELD_CX) * lineProgress}
                y2={SHIELD_CY + (by - SHIELD_CY) * lineProgress}
                stroke={COLORS.borderLight}
                strokeWidth={1}
                strokeDasharray="3 5"
                opacity={lineOpacity}
              />
            );
          })}
        </svg>

        {/* Security Badges */}
        {BADGES.map((badge) => {
          const rad = (badge.angle * Math.PI) / 180;
          const finalX = SHIELD_CX + Math.cos(rad) * BADGE_RADIUS;
          const finalY = SHIELD_CY + Math.sin(rad) * BADGE_RADIUS;

          const badgeProgress = spring({
            frame: frame - badge.delay,
            fps,
            config: SPRING.bouncy,
          });
          const badgeOpacity = interpolate(badgeProgress, [0, 1], [0, 1]);
          const currentX = interpolate(badgeProgress, [0, 1], [SHIELD_CX, finalX]);
          const currentY = interpolate(badgeProgress, [0, 1], [SHIELD_CY, finalY]);

          return (
            <div
              key={badge.label}
              style={{
                position: "absolute",
                left: currentX - 100,
                top: currentY - 30,
                width: 200,
                background: COLORS.bgCard,
                border: `1px solid ${COLORS.borderLight}`,
                borderRadius: 12,
                padding: "12px 18px",
                opacity: badgeOpacity,
              }}
            >
              <div
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: 14,
                  color: COLORS.textPrimary,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <span style={{ fontSize: 16 }}>{badge.icon}</span>
                {badge.label}
              </div>
              <div
                style={{
                  fontFamily: FONTS.body,
                  fontSize: 11,
                  color: COLORS.textMuted,
                  marginTop: 4,
                }}
              >
                {badge.sub}
              </div>
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
